import React, { Fragment, useState } from "react";
import Select from "react-select";
import { Form, Button, Table, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Breadcrumbs } from "../../AbstractElements";
import { Card, CardBody, Col } from "reactstrap";
import { useTranslation } from "react-i18next";
import "./AddInventory.scss";
import { useLoader } from "../../_helper/LoaderContext";
import { usePharmacy } from "../../_helper/PharmacyProvider";
import AsyncSelect from "react-select/async";
import { itemsMaster } from "../PharmacyStocks/StockService";
import { inventoryAdjustment } from "./InventoryService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDisableScrollOnFocus } from "../../Hooks/useDisableScrollOnFocus";

const AdjustInventory = () => {
	const { t } = useTranslation();
	const [reason, setReason] = useState("");
	const { loading, setLoading } = useLoader();
	const { pharmacyPOS } = usePharmacy();
	const [items, setItems] = useState([]);
	// const [note, setNote] = useState("");
	const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
	const [errors, setErrors] = useState({});
	const history = useNavigate();
	const disableScrollProps = useDisableScrollOnFocus();

	const loadMedicines = async (inputValue) => {
		if (!inputValue) return [];

		const body = {
			search: inputValue,
			clinicId: pharmacyPOS?.hospital_id,
		};

		try {
			setLoading(true);
			const response = await itemsMaster(body);
			const medicines = response.data.data || [];

			return medicines.map((m) => ({
				value: m._id,
				label: `${m.brandName} ${!m.isOTC ? ` [${m.saltName}]` : ""}`,
				raw: m // keep full object for later use
			}));
		} catch (error) {
			console.error("Error fetching medicines:", error);
			return [];
		} finally {
			setLoading(false);
		}
	};

	const [searchQuery, setSearchQuery] = useState('');

	const handleMedicineSearchChange = (newValue) => {
		setSearchQuery(newValue);
	};

	// ✅ When a medicine is selected from AsyncSelect
	const handleSelectMedicine = (selectedOption) => {
		if (!selectedOption) return;

		const medicine = selectedOption.raw;

		// Avoid duplicates
		setItems((prev) => {
			const exists = prev.find((i) => i._id === medicine._id);
			if (exists) return prev;

			return [
				...prev,
				{
					...medicine,
					selectedBatchId: "", // user must select
					stkQuantity: 1,         // default qty
				},
			];
		});
	};

	const handleBatchChange = (itemId, batchId) => {
		setItems((prev) =>
			prev.map((item) =>
				item._id === itemId ? { ...item, selectedBatchId: batchId } : item
			)
		);
	};

	// Update quantity
	const updateQuantity = (itemId, value) => {
		setItems((prev) =>
			prev.map((item) =>
				item._id === itemId
					? { ...item, stkQuantity: Math.max(1, Number(value)) }
					: item
			)
		);
	};

	const removeItem = (_id) => {
		setItems((prev) => prev.filter((item) => item._id !== _id));
	};

	// validate form
	const validateForm = () => {
		let newErrors = {};
		if (!reason.trim()) newErrors.reason = "Reason is required";
		if (!date) newErrors.date = "Date is required";
		if (items.length === 0) newErrors.items = "At least one medicine required";

		const itemErrors = [];

		items.forEach((item, index) => {
			const errors = {};

			// Batch number check
			if (!item.selectedBatchId || item.selectedBatchId.trim() === "") {
				errors.selectedBatchId = "Required field";
			}

			itemErrors.push(errors);
		});

		// Check if any item has errors
		const hasItemErrors = itemErrors.some(err => Object.keys(err).length > 0);

		if (hasItemErrors) {
			newErrors.itemErrors = itemErrors;
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSave = async (e) => {
		e.preventDefault();
		if (!validateForm()) return;

		const inventoryItems = items.map((item) => {
			const batch = item.stockData.find(
				(b) => b._id === item.selectedBatchId
			);

			return {
				itemId: item._id,
				stockId: batch?._id,
				code: item.code,
				brandName: item.brandName,
				batchNumber: batch?.batchNumber,
				quantity: item.stkQuantity,
				package: item.package,
				medicineName: item.saltName,
				adjustmentType: "deduct",
				manufacturer: item.manufacturer,
			};
		});

		const payload = {
			clinicId: pharmacyPOS?.hospital_id,
			adjustmentDate: date,
			// note: "sample stock data",
			adjustmentReason: reason,
			adjustmentType: "deduct",
			inventoryItems,
		};

		try {
			setLoading(true);
			const response = await inventoryAdjustment(payload);
			toast.success(response.data.message);
			// resetForm();
			gotoList();
		} catch (error) {
			console.error("Error preparing payload:", error);
		} finally {
			setLoading(false);
		}
	};

	const gotoList = () => {
		history(`${process.env.PUBLIC_URL}/inventory-history`);
	}

	const isDark = document.body.classList.contains("dark-only");
	return (
		<Fragment>
			<Breadcrumbs
				mainTitle={t("Deduct Inventory")}
				parent={t("Dashboard")}
				title={t("Deduct Inventory")}
			/>
			<Col sm="12" className="return-inventory-theme">
				<Card>
					<CardBody>
						<Form onSubmit={handleSave}>
							<div className="row inventory-adjustment">
								<div className="col-md-6">
									<Form.Group className="mb-3">
										<Form.Label>{t('Adjustment Reason')} *</Form.Label>
										<Form.Control
											type="text"
											value={reason}
											onChange={(e) => setReason(e.target.value)}
											isInvalid={!!errors.reason}
											placeholder={t("Enter a reason")}
											required
										/>
										<Form.Control.Feedback type="invalid">
											{errors.reason}
										</Form.Control.Feedback>
									</Form.Group>
								</div>

								<div className="col-md-6">
									<Form.Group className="mb-3">
										<Form.Label>{t('Select Item')} *</Form.Label>
										{/* <Select
																			options={medicineOptions}
																			onChange={handleSelectMedicine}
																			placeholder={t("Search & select a medicine")}
																			isClearable
																		/> */}
										<AsyncSelect
											cacheOptions
											loadOptions={loadMedicines}
											defaultOptions={[]} // nothing until search
											onChange={handleSelectMedicine}
											placeholder={t("Search & select an item")}
											onInputChange={handleMedicineSearchChange}
											noOptionsMessage={() =>
												searchQuery
													? t("We couldn't find any medications matching your search. Please try a different name.")
													: t('Start typing to search...')
											}
											isClearable={true}
											theme={(theme) => ({
												...theme,
												colors: {
													...theme.colors,
													primary25: isDark ? "#373737" : "#e6f7ffff",

												},
											})}
										/>
										{errors.items && (
											<div className="invalid-feedback d-block">
												{errors.items}
											</div>
										)}
									</Form.Group>
								</div>

								{/* Table */}
								<div className="col-md-12">
									{items.length > 0 && (
										<div className="table-responsive table-wrap mt-3 mb-3 mx-3">
											<Table size="sm" className="shadow-sm rounded table-bordered">
												<thead>
													<tr>
														<th>#</th>
														<th>{t('Batch Number')}</th>
														<th>{t('Product Name')}</th>
														<th>{t('Brand Name')}</th>
														<th>{t('Manufacturer')}</th>
														<th>{t('Stock')}</th>
														<th>{t('Quantity')}</th>
														<th>{t('Action')}</th>
													</tr>
												</thead>
												<tbody>
													{items.map((item, index) => (
														<tr key={item.id}>
															<td>{index + 1}</td>
															<td>
																<Form.Select
																	size="sm"
																	value={item.selectedBatchId}
																	onChange={(e) => handleBatchChange(item._id, e.target.value)}
																	isInvalid={errors.itemErrors?.[index]?.selectedBatchId}
																>
																	<option value="">{t("Select Batch")}</option>
																	{item.stockData?.map((batch) => (
																		<option key={batch._id} value={batch._id}>
																			{batch.batchNumber}
																		</option>
																	))}
																</Form.Select>
																{errors.itemErrors?.[index]?.selectedBatchId && (
																	<div className="invalid-feedback d-block">
																		{errors.itemErrors[index].selectedBatchId}
																	</div>
																)}
															</td>
															<td style={{ maxWidth: "120px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
																<OverlayTrigger
																	placement="top"
																	overlay={<Tooltip id={`tooltip-${item._id}`}>{item.brandName}</Tooltip>}
																>
																	<span>{item.brandName}</span>
																</OverlayTrigger>
															</td>
															<td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
																<OverlayTrigger
																	placement="top"
																	overlay={<Tooltip id={`tooltip-${item._id}`}>{item.saltName}</Tooltip>}
																>
																	<span>{item.saltName}</span>
																</OverlayTrigger>
															</td>
															<td style={{ maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
																<OverlayTrigger
																	placement="top"
																	overlay={<Tooltip id={`tooltip-${item._id}`}>{item.manufacturer}</Tooltip>}
																>
																	<span>{item.manufacturer}</span>
																</OverlayTrigger>
															</td>
															<td>
																<span className="badge bg-warning">
																	{item.totalRemainingMedicine}
																</span>
															</td>
															<td>
																<div className="input-group custom-qty-input table-price-counter-nowrap">
																	<button
																		type="button"
																		className="button-minus icon-shape icon-sm btn-danger"
																		onClick={() => updateQuantity(item._id, item.stkQuantity - 1)}
																	>
																		-
																	</button>
																	<input
																		type="number"
																		value={item.stkQuantity}
																		className="quantity-field border-0 incrementor"
																		onChange={(e) => updateQuantity(item._id, e.target.value)}
																		{...disableScrollProps}
																	/>
																	<button
																		type="button"
																		className="button-plus icon-shape icon-sm btn-primary"
																		onClick={() => updateQuantity(item._id, item.stkQuantity + 1)}
																	>
																		+
																	</button>
																</div>
															</td>
															<td className="text-end">
																<button
																	type="button"
																	className="btn-danger btn-sm remove-item"
																	onClick={() => removeItem(item._id)}
																>
																	&times;
																</button>
															</td>
														</tr>
													))}
												</tbody>
											</Table>
										</div>
									)}
								</div>

								{/* Date */}
								<div className="col-md-6">
									<Form.Group className="mb-3">
										<Form.Label>{t('Adjustment Date')} *</Form.Label>
										<Form.Control
											type="date"
											value={date}
											onChange={(e) => setDate(e.target.value)}
											isInvalid={!!errors.date}
											required
										/>
										<Form.Control.Feedback type="invalid">
											{errors.date}
										</Form.Control.Feedback>
									</Form.Group>
								</div>
							</div>

							{/* Buttons */}
							<div className="d-flex justify-content-end gap-2">
								<Button variant="danger" type="submit">
									{t('Submit Adjustment')}
								</Button>
								<Button
									variant="secondary"
									onClick={() => {
										setReason("");
										setItems([]);
										// setNote("");
										setDate(new Date().toISOString().split("T")[0]);
										setErrors({});
									}}
								>
									{t('Reset')}
								</Button>
							</div>
						</Form>
					</CardBody>
				</Card>
			</Col>
		</Fragment>
	);
};

export default AdjustInventory;
